import { useCallback, useEffect, useState } from 'react';
import {
  getPosition, heldPosition, mayReadWithoutAsking, permissionState,
} from '../utils/devicePosition';

/**
 * Where the phone is, for a screen that wants it — see utils/devicePosition.
 *
 * `status`:
 * - 'checking'    asking the OS whether we may (brief; render nothing)
 * - 'prompt'      we may not yet — offer a tap that calls `request`
 * - 'locating'    getting a fix
 * - 'ready'       `position` is set
 * - 'failed'      the user asked and no fix came (timeout, Location off)
 * - 'denied' / 'unavailable'  nothing to offer; render nothing
 *
 * Mounting never prompts for a question nobody has answered yes to. Only
 * `request()` can, and only a tap should call it.
 */
export function useDevicePosition() {
  const [position, setPosition] = useState(heldPosition);
  const [status, setStatus] = useState(() => (heldPosition() ? 'ready' : 'checking'));

  useEffect(() => {
    if (heldPosition()) return undefined;
    let live = true;
    (async () => {
      const state = await permissionState();
      if (!live) return;
      // Home has usually been allowed already; a device that said yes before
      // reads on even where Safari has forgotten (utils/devicePosition).
      if (!mayReadWithoutAsking(state)) { setStatus(state); return; }
      setStatus('locating');
      const fix = await getPosition();
      if (!live) return;
      if (fix) { setPosition(fix); setStatus('ready'); return; }
      // No fix. If that was a remembered yes the OS turned down, say what it
      // says now — a tap to offer, or nothing.
      const after = state === 'granted' ? state : await permissionState();
      if (!live) return;
      setStatus(after === 'granted' ? 'unavailable' : after);
    })();
    return () => { live = false; };
  }, []);

  const request = useCallback(async () => {
    setStatus('locating');
    const fix = await getPosition({ prompt: true });
    if (fix) {
      setPosition(fix);
      setStatus('ready');
      return;
    }
    // A refusal the user just made needs no message; a fix that never came does.
    setStatus((await permissionState()) === 'denied' ? 'denied' : 'failed');
  }, []);

  return { status, position, request };
}
